import { getSupabase } from './client';
import { requireUserId } from './auth';
import { putJson, type MeetingKeys } from '../storage/s3';
import { getPeopleNames, resolveParticipants } from './directoryRepo';
import { t } from '../language';
import { isRecordingPath, resolveRecordingPath } from '../recordings';
import { embeddedSegments, openMeetingRow, openMeetingRows } from '../crypto/openContent';
import { contentSealer, type ContentSealer } from '../crypto/sealContent';
import { getContentKeyInfo } from '../crypto/contentKey';
import type {
  ActionItemDTO,
  ImportProvider,
  CraftBlockDTO,
  GeneratedSummary,
  MeetingBotDTO,
  MeetingDTO,
  MeetingParticipantDTO,
  TranscriptSegmentDTO,
} from '../../shared/types';

/**
 * Toplantı deposu — arayüzün tek parça `MeetingDTO`'su ile dört tabloya
 * (meetings, transcripts, action_items, craft_blocks + meeting_participants)
 * dağılmış satırlar arasında çeviri yapar.
 *
 * Erişim denetimi tamamen RLS'te: her sorgu oturumun JWT'siyle gidiyor,
 * `user_id = auth.uid()` politikası başkasının verisini görünmez kılıyor.
 * Buradaki `user_id` atamaları yetki için değil, satırı sahiplendirmek için.
 */

function statusFromDb(value: string | null): MeetingDTO['status'] {
  if (value === 'processing' || value === 'transcribing' || value === 'summarizing') {
    return 'in-progress';
  }
  if (value === 'queued' || value === 'uploading') return 'upcoming';
  return 'recorded';
}

/** S3'e arşivlenmiş bir toplantının nesne anahtarları. Ses yoksa `audioKey` boş. */
export type StoredS3Keys = Omit<MeetingKeys, 'audioKey'> & { audioKey?: string };

/** Skribby botunun kalıcı durumu. */
export interface StoredBot {
  /** Skribby bot kimliği — bot oluşturulana kadar boş. */
  id?: string;
  /** Skribby'nin ham durumu ya da `creating` / `summarizing` / `completed`. */
  status: string;
  meetingUrl: string;
  stopReason?: string | null;
  /** Bitmiş botun işlenmesi (indir → özetle → S3) kaç kez başarısız oldu. */
  finalizeAttempts?: number;
}

/**
 * meetings.options içinde taşınan, kendi kolonu olmayan alanlar.
 *
 * DİKKAT: options tek bir jsonb; bir alanı yazarken diğerlerini ezmemek için
 * her zaman `patchOptions` ile oku-birleştir-yaz yapılmalı.
 */
export interface MeetingOptions {
  decisions?: string[];
  audioFilePath?: string;
  s3?: StoredS3Keys;
  /**
   * Kayıt S3'e taşınmayı bekliyor. Yalnızca bu özellikten sonra oluşturulan
   * kayıtlarda açılıyor — eski yerel kayıtlar kendiliğinden taşınıp
   * diskten silinmesin.
   */
  archivePending?: boolean;
  bot?: StoredBot;
  /** Takvimden gelen toplantının etkinlik kimliği — aynı etkinliğe ikinci bot gönderilmesin. */
  calendarEventId?: string;
  /** Transkriptteki konuşmacı etiketi → kişi kimliği (bkz. setSpeakerPerson). */
  speakerMap?: Record<string, string>;
  /** Sunucunun bot toplantısını tamamlayınca masaüstüne bıraktığı işler (botSync.ts). */
  clientTasks?: BotClientTasks;
  /** Başka bir uygulamadan içe aktarıldıysa kaynağı (bkz. services/imports). */
  importSource?: MeetingDTO['importSource'];
  /**
   * Şifreleme seviyesi (web/lib/settings/app-settings.ts > EncryptionLevel): 1 içerik,
   * 2 + başlık ve katılımcılar, 3 + konuşmacı eşlemesi şifreli yazıldı — sunucudaki dönüştürme işi atlar
   * (web/lib/jobs/encrypt-backfill.ts). Yok: düz ya da eksik yazıldı, iş yeniden ele alır.
   */
  enc?: 1 | 2 | 3;
}

/** Sunucudaki bot tamamlama işinin masaüstüne bıraktığı işler (web/lib/jobs/bots.ts → ClientTasks). */
export interface BotClientTasks {
  /** Geçici hatadan sonra yapılan deneme sayısı ve bir sonraki deneme zamanı (masaüstü yazar). */
  attempts?: number;
  nextAttemptAt?: string;
  participants?: MeetingParticipantDTO[];
  summaryEmail?: {
    title: string;
    durationSeconds: number;
    summary: Pick<GeneratedSummary, 'tldr' | 'keyPoints' | 'decisions'>;
    actionItems: GeneratedSummary['actionItems'];
  };
}

interface MeetingRow {
  id: string;
  title: string;
  status: string | null;
  duration_seconds: number | string | null;
  recorded_at: string | null;
  created_at: string;
  summary: string | null;
  key_points: unknown;
  options: unknown;
  space: string | null;
  tags: string[] | null;
  error_message: string | null;
  folder_id?: string | null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function parseOptions(value: unknown): MeetingOptions {
  return value && typeof value === 'object' ? (value as MeetingOptions) : {};
}

// ————————————————————————————————————————————————
// Okuma
// ————————————————————————————————————————————————

/**
 * Kullanıcının tüm toplantılarını, çizmek için gereken her şeyle birlikte
 * getirir. İlişkili tablolar tek sorguda gömülü olarak çekiliyor — toplantı
 * başına ayrı istek atan N+1 deseninden kaçınmak için.
 */
export async function listMeetings(): Promise<MeetingDTO[]> {
  const userId = await requireUserId();
  const { data, error } = await getSupabase()
    .from('meetings')
    .select(`
      id, title, status, duration_seconds, recorded_at, created_at, folder_id,
      summary, key_points, options, space, tags, error_message,
      transcripts ( segments ),
      action_items ( id, text, assignee, is_done, due_date, position ),
      craft_blocks ( id, type, content, checked, tag, position ),
      meeting_participants ( name, role, company, position, email, person_id )
    `)
    .eq('user_id', userId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[meetingsRepo] listMeetings:', error.message);
    throw new Error(t('Toplantılar yüklenemedi.', 'Could not load meetings.'));
  }
  // Şifreli alanlar (tn1) çözülür; düz kayıtlar olduğu gibi kalır.
  const rows = await openMeetingRows(data ?? [], userId);

  // Konuşmacı eşlemelerindeki kişi adları tek sorguda çözülüyor — kişi
  // yeniden adlandırılınca transkriptte güncel ad görünsün.
  const personIds = new Set<string>();
  for (const row of rows) {
    const map = parseOptions((row as any).options).speakerMap;
    if (map) Object.values(map).forEach((id) => personIds.add(id));
  }
  const names = await getPeopleNames([...personIds]);

  return rows.map((row) => rowToDTO(row, names));
}

function botToDTO(bot: StoredBot | undefined, errorMessage: string | null): MeetingBotDTO | undefined {
  if (!bot) return undefined;
  return {
    status: bot.status,
    meetingUrl: bot.meetingUrl,
    error: errorMessage ?? undefined,
  };
}

function rowToDTO(row: any, personNames = new Map<string, string>()): MeetingDTO {
  const meeting = row as MeetingRow;
  const options = parseOptions(meeting.options);

  const transcript = embeddedSegments(row.transcripts);
  const actionItems = (row.action_items ?? []) as Array<{
    id: string; text: string; assignee: string | null;
    is_done: boolean; due_date: string | null; position: number;
  }>;
  const craftBlocks = (row.craft_blocks ?? []) as Array<{
    id: string; type: string; content: string;
    checked: boolean; tag: string | null; position: number;
  }>;
  const participants = (row.meeting_participants ?? []) as Array<{
    name: string; role: string | null; company: string | null; position: number;
    email: string | null; person_id: string | null;
  }>;

  return {
    id: meeting.id,
    title: meeting.title,
    recordedAtISO: meeting.recorded_at ?? meeting.created_at,
    durationSeconds: Number(meeting.duration_seconds ?? 0),
    status: statusFromDb(meeting.status),
    space: meeting.space === 'shared' ? 'shared' : 'private',
    folderId: meeting.folder_id ?? null,
    tags: meeting.tags ?? [],
    participants: participants
      .sort((a, b) => a.position - b.position)
      .map((p) => ({
        name: p.name,
        role: p.role ?? undefined,
        company: p.company ?? undefined,
        email: p.email ?? undefined,
        personId: p.person_id ?? undefined,
      })),
    summary: {
      tldr: meeting.summary ?? '',
      keyPoints: asStringArray(meeting.key_points),
      decisions: asStringArray(options.decisions),
    },
    actionItems: actionItems
      .sort((a, b) => a.position - b.position)
      .map((item) => ({
        id: item.id,
        text: item.text,
        assignee: item.assignee ?? '',
        completed: item.is_done,
        dueDate: item.due_date ?? undefined,
      })),
    craftBlocks: craftBlocks
      .sort((a, b) => a.position - b.position)
      .map((block) => ({
        id: block.id,
        type: block.type as CraftBlockDTO['type'],
        content: block.content,
        checked: block.checked,
        tag: block.tag ?? undefined,
      })),
    transcript: Array.isArray(transcript) ? (transcript as TranscriptSegmentDTO[]) : [],
    // Kayıt klasörü dışını gösteren yol arayüze (oynatma) hiç çıkmaz (SEC-07).
    audioFilePath: ((p) => (isRecordingPath(p) ? p : undefined))(resolveRecordingPath(options.audioFilePath)),
    storage: options.s3 ? 's3' : 'local',
    bot: botToDTO(options.bot, meeting.error_message),
    speakerMap: options.speakerMap
      ? Object.fromEntries(
          Object.entries(options.speakerMap)
            .filter(([, personId]) => personNames.has(personId))
            .map(([label, personId]) => [label, { personId, name: personNames.get(personId)! }])
        )
      : undefined,
  };
}

// ————————————————————————————————————————————————
// Yazma
// ————————————————————————————————————————————————

/**
 * Yeni bir toplantı oluşturur ve tüm alt kayıtlarını yazar.
 * Veritabanının ürettiği gerçek kimlikleri taşıyan DTO'yu döndürür —
 * arayüz geçici (`meeting-<timestamp>`) kimliklerle çalışmaya devam etmesin.
 */
export async function createMeeting(input: Omit<MeetingDTO, 'id'>): Promise<MeetingDTO> {
  const supabase = getSupabase();
  const userId = await requireUserId();
  // Renderer'dan gelen yol yalnızca kayıt klasöründeki bir ses dosyasıysa saklanır (SEC-07).
  const audioFilePath = isRecordingPath(input.audioFilePath) ? input.audioFilePath : undefined;

  // İçerik şifreleme açıksa (tn1) şifre bağlamı toplantı kimliğine bağlı: satır önce
  // içeriksiz açılır, şifreli özet kimlik belli olunca yazılır.
  // Durum öğrenilemezse düz yazılır (kayıt kaybolmasın); dönüştürme işi sonra şifreler.
  const info = await getContentKeyInfo(userId).catch(() => null);
  const encrypt = Boolean(info?.writeEncrypted && info.key);
  // 2. seviyede başlık da şifreli: satır varsayılan başlıkla açılır, şifreli başlık ardından yazılır.
  const sealTitle = encrypt && (info?.level ?? 0) >= 2;
  const options: MeetingOptions = {
    decisions: encrypt ? undefined : input.summary.decisions,
    audioFilePath: audioFilePath,
    archivePending: audioFilePath ? true : undefined,
    importSource: input.importSource,
    ...(encrypt ? { enc: Math.max(1, info?.level ?? 1) as 1 | 2 | 3 } : {}),
  };

  const { data, error } = await supabase
    .from('meetings')
    .insert({
      user_id: userId,
      ...(sealTitle ? {} : { title: input.title }),
      // Veritabanı istemcinin yalnızca tamamlanmış masaüstü kaydı açmasına izin veriyor (0027).
      status: 'completed',
      source: 'desktop',
      media_type: 'audio',
      mime_type: 'audio/webm',
      duration_seconds: input.durationSeconds,
      recorded_at: input.recordedAtISO,
      summary: encrypt ? null : input.summary.tldr,
      key_points: encrypt ? null : input.summary.keyPoints,
      options,
      space: input.space,
      tags: input.tags,
    })
    .select('id')
    .single();

  if (error || !data) {
    console.error('[meetingsRepo] createMeeting:', error?.message);
    throw new Error(t('Toplantı kaydedilemedi.', 'Could not save the meeting.'));
  }

  const meetingId = data.id as string;
  const sealer = await contentSealer(userId, meetingId);

  if (encrypt) {
    const { error: sealError } = await supabase
      .from('meetings')
      .update({
        ...(sealTitle ? { title: await sealer.field('meetings.title', input.title) } : {}),
        summary: await sealer.field('meetings.summary', input.summary.tldr),
        key_points: await sealer.json('meetings.key_points', input.summary.keyPoints),
        options: { ...options, decisions: await sealer.json('meetings.decisions', input.summary.decisions) },
      })
      .eq('id', meetingId);
    if (sealError) {
      console.error('[meetingsRepo] createMeeting (şifreli özet):', sealError.message);
      // Özetsiz yarım satır kalmasın.
      await deleteMeetings([meetingId]).catch(() => {});
      throw new Error(t('Toplantı kaydedilemedi.', 'Could not save the meeting.'));
    }
  }

  await Promise.all([
    writeTranscript(meetingId, input.transcript, sealer),
    replaceActionItems(meetingId, input.actionItems, sealer),
    replaceCraftBlocks(meetingId, input.craftBlocks, sealer),
    replaceParticipants(meetingId, input.participants),
  ]);

  return { ...input, audioFilePath, id: meetingId, storage: 'local' };
}

/**
 * Bu kaynaktan daha önce aktarılmış notların kimlikleri (çöp kutusundakiler
 * dahil — kullanıcı sildiği notun yeniden gelmesini istemez).
 */
export async function listImportedIds(provider: ImportProvider): Promise<Set<string>> {
  const { data, error } = await getSupabase()
    .from('meetings')
    .select('source_id:options->importSource->>id')
    .eq('user_id', await requireUserId())
    .eq('options->importSource->>provider', provider);
  if (error) {
    console.error('[meetingsRepo] listImportedIds:', error.message);
    throw new Error(t('Toplantılar okunamadı.', 'Could not read meetings.'));
  }
  return new Set(
    (data ?? []).map((row) => (row as { source_id: unknown }).source_id).filter((id): id is string => typeof id === 'string')
  );
}

/**
 * Var olan bir toplantıyı günceller.
 *
 * Alt koleksiyonlar (aksiyon maddeleri, bloklar) "sil ve yeniden yaz"
 * yöntemiyle güncelleniyor. Arayüz bunları her zaman tam liste olarak
 * gönderdiği için bu, tek tek fark hesaplamaktan hem daha basit hem de
 * sıralama tutarlılığını garanti ediyor.
 */
export async function updateMeeting(meeting: MeetingDTO): Promise<{ stale: boolean }> {
  const supabase = getSupabase();

  // options birden fazla alt sistemin (S3 arşivi, bot) alanlarını taşıyor —
  // arayüzün bilmediği alanlar ezilmesin diye önce mevcut hâli okunuyor.
  const { data: current, error: readError } = await supabase
    .from('meetings')
    .select('id, options, summary, key_points, status')
    .eq('id', meeting.id)
    .single();

  if (readError || !current) {
    console.error('[meetingsRepo] updateMeeting (okuma):', readError?.message);
    throw new Error(t('Toplantı güncellenemedi.', 'Could not update the meeting.'));
  }

  // İçerik şifreleme açıksa yazılanlar tn1 ile şifrelenir; karşılaştırma çözülmüş hâliyle.
  const userId = await requireUserId();
  const sealer = await contentSealer(userId, meeting.id);
  const opened = await openMeetingRow(current, userId);

  // Arayüzdeki kopya bayat: sunucu bu arada durumu değiştirmiş (ör. bot
  // toplantısını tamamlamış). Sunucunun ürettiği özet, transkript, aksiyonlar
  // ve katılımcılar eski kopyayla ezilmesin; yalnızca kullanıcı alanları yazılır
  // ve arayüz yenilenir (BUG-01). Durum/süre/tarih zaten istemciden yazılmıyor (0027).
  const stale = statusFromDb(current.status) !== meeting.status;

  const options = parseOptions(current.options);
  const nextOptions: MeetingOptions = {
    ...options,
    ...(stale ? {} : { decisions: (await sealer.json('meetings.decisions', meeting.summary.decisions)) as string[] }),
    // Yerel ses yolu arayüzden alınmaz (SEC-07): yalnızca kayıt açılırken,
    // kayıt klasörü doğrulanarak yazılıyor. Arşivlenince bilerek silindi.
    audioFilePath: options.s3 ? undefined : options.audioFilePath,
    // Düz (ya da daha düşük seviyede) yazıldıysa "şifreli" işareti düşer/iner: dönüştürme
    // işi toplantıyı yeniden ele alır.
    enc: sealer.enabled && options.enc ? (Math.min(options.enc, sealer.level) as 1 | 2 | 3) : undefined,
  };

  const { error } = await supabase
    .from('meetings')
    .update({
      title: await sealer.field('meetings.title', meeting.title),
      ...(stale
        ? {}
        : {
            summary: await sealer.field('meetings.summary', meeting.summary.tldr),
            key_points: await sealer.json('meetings.key_points', meeting.summary.keyPoints),
          }),
      options: nextOptions,
      space: meeting.space,
      tags: meeting.tags,
    })
    .eq('id', meeting.id);

  if (error) {
    console.error('[meetingsRepo] updateMeeting:', error.message);
    throw new Error(t('Toplantı güncellenemedi.', 'Could not update the meeting.'));
  }

  if (stale) {
    await replaceCraftBlocks(meeting.id, meeting.craftBlocks, sealer);
    return { stale: true };
  }

  await Promise.all([
    // S3'teki toplantıların transkripti listede hiç yüklenmiyor (boş dizi);
    // onu veritabanına yazmak transkripti silmek olurdu.
    options.s3 ? Promise.resolve() : writeTranscript(meeting.id, meeting.transcript, sealer),
    replaceActionItems(meeting.id, meeting.actionItems, sealer),
    replaceCraftBlocks(meeting.id, meeting.craftBlocks, sealer),
    replaceParticipants(meeting.id, meeting.participants),
  ]);

  // Özet elle düzenlendiyse S3'teki kopya da güncel kalsın.
  if (options.s3) {
    const before = parseOptions(opened.options);
    const summaryChanged =
      opened.summary !== meeting.summary.tldr ||
      JSON.stringify(opened.key_points ?? []) !== JSON.stringify(meeting.summary.keyPoints) ||
      JSON.stringify(before.decisions ?? []) !== JSON.stringify(meeting.summary.decisions);
    if (summaryChanged) {
      const summary: GeneratedSummary = {
        ...meeting.summary,
        actionItems: meeting.actionItems.map((item) => ({ text: item.text, assignee: item.assignee })),
      };
      await putJson(options.s3.summaryKey, summary).catch((err) =>
        console.error('[meetingsRepo] summary.json güncellenemedi:', err)
      );
    }
  }
  return { stale: false };
}

/**
 * Yumuşak silme — `deleted_at` doldurulur, satır durur.
 *
 * Düz bir `.update()` yerine `soft_delete_meetings` RPC'sini çağırıyoruz.
 * Sebep: `meetings_owner_all` bir `FOR ALL` politikası ve Postgres bunu
 * içeride SELECT/INSERT/UPDATE/DELETE için ayrı politikalara açıyor — SELECT
 * bileşeninin USING'i (`deleted_at is null`) UPDATE sırasında yeni satıra
 * karşı da uygulanıyor, yani WITH CHECK doğru olsa bile `deleted_at`'i
 * dolduran bir UPDATE "artık kendi SELECT politikanla göremeyeceğin bir
 * satır üretemezsin" kısıtına takılıp RLS ihlali veriyor. Bu, politikayı
 * ayrı SELECT/UPDATE politikalarına bölerek de çözülmüyor (bkz.
 * migrations/0011_soft_delete_rpc.sql). Çözüm: soft-delete'i RLS'i atlayan
 * SECURITY DEFINER bir fonksiyona taşımak, sahiplik kontrolünü fonksiyonun
 * içinde elle yapmak. Fonksiyon, gerçekten silinen satırların id'lerini
 * `returning id` ile döndürüyor — isteneni karşılaştırıp uyuşmazlığı gerçek
 * bir hataya çeviriyoruz (ör. süresi dolmuş oturum, başka kullanıcının
 * kimliği).
 */
export async function deleteMeetings(ids: string[]): Promise<void> {
  if (ids.length === 0) return;

  const { data: deletedIds, error } = await getSupabase().rpc('soft_delete_meetings', {
    ids,
  });

  if (error) {
    console.error('[meetingsRepo] deleteMeetings:', error);
    throw new Error(
      t(
        `Toplantılar silinemedi. (${error.code ?? '?'}: ${error.message}${error.details ? ' — ' + error.details : ''}${error.hint ? ' — ' + error.hint : ''})`,
        `Could not delete the meetings. (${error.code ?? '?'}: ${error.message}${error.details ? ' — ' + error.details : ''}${error.hint ? ' — ' + error.hint : ''})`
      )
    );
  }

  const affected = deletedIds?.length ?? 0;
  if (affected !== ids.length) {
    console.error('[meetingsRepo] deleteMeetings: beklenen', ids.length, 'satır, etkilenen', affected);
    throw new Error(
      t(
        `${ids.length - affected} toplantı silinemedi — oturumunuz bu kayıtlara erişemiyor olabilir. Lütfen yeniden giriş yapıp tekrar deneyin.`,
        `${ids.length - affected} meeting(s) could not be deleted — your session may not have access to those records. Please sign in again and retry.`
      )
    );
  }
}

async function writeTranscript(
  meetingId: string,
  segments: TranscriptSegmentDTO[],
  sealer: ContentSealer
): Promise<void> {
  const fullText = segments.map((s) => `${s.speaker}: ${s.text}`).join('\n');

  // transcripts.meeting_id benzersiz — upsert doğal olarak 1:1 ilişkiyi korur.
  const { error } = await getSupabase()
    .from('transcripts')
    .upsert(
      {
        meeting_id: meetingId,
        segments: await sealer.json('transcripts.segments', segments),
        full_text: await sealer.field('transcripts.full_text', fullText),
        language: 'tr',
      },
      { onConflict: 'meeting_id' }
    );

  if (error) console.error('[meetingsRepo] writeTranscript:', error.message);
}

async function replaceActionItems(meetingId: string, items: ActionItemDTO[], sealer: ContentSealer): Promise<void> {
  const supabase = getSupabase();
  // Önce şifrele, sonra sil-yaz: şifreleme hata verirse eski satırlar yerinde kalır.
  const rows = await Promise.all(
    items.map(async (item, index) => ({
      meeting_id: meetingId,
      text: await sealer.field('action_items.text', item.text),
      assignee: await sealer.field('action_items.assignee', item.assignee || null),
      is_done: item.completed,
      source: 'user',
      position: index,
    }))
  );
  await supabase.from('action_items').delete().eq('meeting_id', meetingId);
  if (rows.length === 0) return;

  const { error } = await supabase.from('action_items').insert(rows);

  if (error) console.error('[meetingsRepo] replaceActionItems:', error.message);
}

async function replaceCraftBlocks(meetingId: string, blocks: CraftBlockDTO[], sealer: ContentSealer): Promise<void> {
  const supabase = getSupabase();
  // Önce şifrele, sonra sil-yaz: şifreleme hata verirse eski satırlar yerinde kalır.
  const rows = await Promise.all(
    blocks.map(async (block, index) => ({
      meeting_id: meetingId,
      type: block.type,
      content: (await sealer.field('craft_blocks.content', block.content)) ?? '',
      checked: block.checked ?? false,
      tag: block.tag ?? null,
      position: index,
    }))
  );
  await supabase.from('craft_blocks').delete().eq('meeting_id', meetingId);
  if (rows.length === 0) return;

  const { error } = await supabase.from('craft_blocks').insert(rows);

  if (error) console.error('[meetingsRepo] replaceCraftBlocks:', error.message);
}

async function replaceParticipants(
  meetingId: string,
  participants: MeetingParticipantDTO[]
): Promise<void> {
  // Katılımcılar kişilere (people) ve şirketlere bağlanarak yazılıyor.
  await resolveParticipants(meetingId, participants, 'replace').catch((err) =>
    console.error('[meetingsRepo] replaceParticipants:', err)
  );
}

// ————————————————————————————————————————————————
// options yardımcıları, S3 arşivi ve bot toplantıları
// ————————————————————————————————————————————————

/** options'ı oku → değiştir → yaz. Ek kolon güncellemeleri `columns` ile. */
export async function patchOptions(
  meetingId: string,
  mutate: (options: MeetingOptions) => MeetingOptions,
  columns: Record<string, unknown> = {}
): Promise<MeetingOptions> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('meetings')
    .select('options')
    .eq('id', meetingId)
    .single();

  if (error || !data) {
    throw new Error(t(`Toplantı okunamadı: ${error?.message ?? 'bulunamadı'}`, `Could not read the meeting: ${error?.message ?? 'not found'}`));
  }

  const next = mutate({ ...parseOptions(data.options) });
  const { error: updateError } = await supabase
    .from('meetings')
    .update({ ...columns, options: next })
    .eq('id', meetingId);

  if (updateError) throw new Error(t(`Toplantı güncellenemedi: ${updateError.message}`, `Could not update the meeting: ${updateError.message}`));
  return next;
}

/** Toplantının S3 anahtarları — RLS sayesinde yalnızca sahibi okuyabilir. */
export async function getMeetingS3Keys(meetingId: string): Promise<StoredS3Keys | null> {
  const { data, error } = await getSupabase()
    .from('meetings')
    .select('options')
    .eq('id', meetingId)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw new Error(t(`Toplantı okunamadı: ${error.message}`, `Could not read the meeting: ${error.message}`));
  if (!data) throw new Error(t('Toplantı bulunamadı.', 'Meeting not found.'));
  return parseOptions(data.options).s3 ?? null;
}

/**
 * Arşiv tamamlandı: anahtarları yaz, yerel yolu düşür, veritabanındaki
 * transkript kopyasını sil (transkriptin tek kaynağı artık S3).
 */
export async function markArchived(meetingId: string, keys: StoredS3Keys): Promise<void> {
  // Düz dosya yazıldıysa "şifreli" işareti düşer: dönüştürme işi dosyaları sonra şifreler.
  const sealed = [keys.audioKey, keys.transcriptKey, keys.summaryKey].every((key) => !key || key.endsWith('.tn1'));
  await patchOptions(meetingId, (options) => {
    const { audioFilePath: _dropped, archivePending: _done, ...rest } = options;
    return { ...rest, s3: keys, enc: sealed ? rest.enc : undefined };
  });

  const { error } = await getSupabase().from('transcripts').delete().eq('meeting_id', meetingId);
  if (error) console.error('[meetingsRepo] markArchived transkript silinemedi:', error.message);
}

export interface PendingArchive {
  meetingId: string;
  audioFilePath: string;
  transcript: TranscriptSegmentDTO[];
  summary: GeneratedSummary;
}

/**
 * Kaydı bu makinede duran ama henüz S3'e arşivlenmemiş yeni toplantılar
 * (arşiv sırasında bağlantı koptuysa ya da uygulama kapandıysa).
 */
export async function listPendingArchives(): Promise<PendingArchive[]> {
  const userId = await requireUserId();
  const { data, error } = await getSupabase()
    .from('meetings')
    .select(`
      id, summary, key_points, options,
      transcripts ( segments ),
      action_items ( text, assignee, position )
    `)
    .eq('user_id', userId)
    .is('deleted_at', null)
    .eq('options->>archivePending', 'true')
    .not('options->>audioFilePath', 'is', null)
    .is('options->s3', null);

  if (error) {
    console.error('[meetingsRepo] listPendingArchives:', error.message);
    return [];
  }

  return (await openMeetingRows(data ?? [], userId)).map((row: any) => {
    const options = parseOptions(row.options);
    const segments = embeddedSegments(row.transcripts);
    const items = ((row.action_items ?? []) as Array<{ text: string; assignee: string | null; position: number }>)
      .sort((a, b) => a.position - b.position);
    return {
      meetingId: row.id as string,
      audioFilePath: resolveRecordingPath(options.audioFilePath as string),
      transcript: Array.isArray(segments) ? (segments as TranscriptSegmentDTO[]) : [],
      summary: {
        tldr: row.summary ?? '',
        keyPoints: asStringArray(row.key_points),
        decisions: asStringArray(options.decisions),
        actionItems: items.map((item) => ({ text: item.text, assignee: item.assignee ?? undefined })),
      },
    };
  })
    // Kayıt klasörü dışını gösteren satırlar arşivlenmez (SEC-07).
    .filter((pending) => isRecordingPath(pending.audioFilePath));
}

/** Bot için yer tutucu toplantı satırı — bot bitince içi doldurulur. */
export async function createBotMeeting(input: {
  title: string;
  meetingUrl: string;
  recordedAtISO: string;
  participants: MeetingParticipantDTO[];
  calendarEventId?: string;
}): Promise<string> {
  const userId = await requireUserId();
  // 2. seviyede başlık şifreli: satır varsayılan başlıkla açılır, şifreli başlık kimlik belli olunca yazılır.
  const info = await getContentKeyInfo(userId).catch(() => null);
  const sealTitle = Boolean(info?.writeEncrypted && info.key && info.level >= 2);
  const { data, error } = await getSupabase()
    .from('meetings')
    .insert({
      user_id: userId,
      ...(sealTitle ? {} : { title: input.title }),
      status: 'queued',
      source: 'bot',
      media_type: 'audio',
      mime_type: 'audio/mpeg',
      duration_seconds: 0,
      recorded_at: input.recordedAtISO,
      summary: '',
      key_points: [],
      options: {
        bot: { status: 'creating', meetingUrl: input.meetingUrl },
        calendarEventId: input.calendarEventId,
      } satisfies MeetingOptions,
      space: 'private',
      tags: ['Bot'],
    })
    .select('id')
    .single();

  if (error || !data) {
    console.error('[meetingsRepo] createBotMeeting:', error?.message);
    throw new Error(t('Bot toplantısı oluşturulamadı.', 'Could not create the bot meeting.'));
  }

  const meetingId = data.id as string;
  if (sealTitle) {
    const sealer = await contentSealer(userId, meetingId);
    const { error: titleError } = await getSupabase()
      .from('meetings')
      .update({ title: await sealer.field('meetings.title', input.title) })
      .eq('id', meetingId);
    if (titleError) console.error('[meetingsRepo] createBotMeeting (şifreli başlık):', titleError.message);
  }
  await replaceParticipants(meetingId, input.participants);
  return meetingId;
}

/** Bu takvim etkinliği için daha önce bot toplantısı açıldı mı (silinmemiş). */
/**
 * "Herkese" özet e-postası için toplantının zamanı ve katılımcıları.
 *
 * `user_id` açıkça süzülüyor: RLS yöneticilere başkalarının katılımcılarını da
 * okutuyor; bir yönetici arayüzü başka birinin toplantı kimliğini gönderse
 * bile o toplantının katılımcılarına posta gitmemeli.
 */
export async function getMeetingShareInfo(meetingId: string): Promise<{
  recordedAtISO: string;
  participantNames: string[];
  participantEmails: string[];
} | null> {
  const userId = await requireUserId();
  const { data, error } = await getSupabase()
    .from('meetings')
    .select('id, recorded_at, created_at, meeting_participants ( name, email )')
    .eq('id', meetingId)
    .eq('user_id', userId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) {
    console.error('[meetingsRepo] getMeetingShareInfo:', error.message);
    return null;
  }
  if (!data) return null;

  // Katılımcı adları ve e-postaları şifreli olabilir (tn1, bağlam: toplantı).
  const opened = await openMeetingRow(data as Record<string, unknown>, userId);
  const participants = ((opened as { meeting_participants?: unknown }).meeting_participants ?? []) as Array<{
    name: string | null;
    email: string | null;
  }>;
  const row = data as { recorded_at: string | null; created_at: string };
  return {
    recordedAtISO: row.recorded_at ?? row.created_at,
    participantNames: participants.map((p) => p.name?.trim() ?? '').filter(Boolean),
    participantEmails: participants.map((p) => p.email ?? '').filter(Boolean),
  };
}

export async function findBotMeetingForEvent(calendarEventId: string): Promise<string | null> {
  const { data, error } = await getSupabase()
    .from('meetings')
    .select('id')
    .eq('source', 'bot')
    .eq('options->>calendarEventId', calendarEventId)
    .eq('user_id', await requireUserId())
    .is('deleted_at', null)
    .limit(1);
  if (error) {
    console.error('[meetingsRepo] findBotMeetingForEvent:', error.message);
    // Emin olamıyorsak "var" say — aynı toplantıya iki bot göndermekten iyidir.
    return 'unknown';
  }
  return (data?.[0]?.id as string | undefined) ?? null;
}

/** Bugün ve sonrası için bot planlanmış takvim etkinliklerinin kimlikleri. */
export async function listScheduledEventIds(sinceISO: string): Promise<Record<string, { meetingId: string; status: string }>> {
  const { data } = await getSupabase()
    .from('meetings')
    .select('id, options, recorded_at')
    .eq('source', 'bot')
    .gte('recorded_at', sinceISO)
    .eq('user_id', await requireUserId())
    .is('deleted_at', null);
  const result: Record<string, { meetingId: string; status: string }> = {};
  for (const row of (data ?? []) as Array<{ id: string; options: unknown }>) {
    const options = parseOptions(row.options);
    if (options.calendarEventId) {
      result[options.calendarEventId] = { meetingId: row.id, status: options.bot?.status ?? 'unknown' };
    }
  }
  return result;
}

export interface ActiveBotMeeting {
  meetingId: string;
  title: string;
  bot: StoredBot;
}

/** Botu hâlâ çalışan (ya da bitmiş ama işlenmemiş) toplantılar. */
export async function listActiveBotMeetings(): Promise<ActiveBotMeeting[]> {
  const userId = await requireUserId();
  const { data, error } = await getSupabase()
    .from('meetings')
    .select('id, title, options')
    .eq('source', 'bot')
    .in('status', ['queued', 'processing', 'summarizing'])
    .eq('user_id', userId)
    .is('deleted_at', null);

  if (error) {
    console.error('[meetingsRepo] listActiveBotMeetings:', error.message);
    return [];
  }

  return (await openMeetingRows(data ?? [], userId))
    .map((row: any) => ({ meetingId: row.id as string, title: row.title as string, bot: parseOptions(row.options).bot }))
    .filter((row): row is ActiveBotMeeting => Boolean(row.bot?.id));
}

/** Sunucunun tamamladığı, masaüstünde yapılacak işi kalmış bot toplantıları. */
export async function listBotClientTasks(): Promise<Array<{ meetingId: string; tasks: BotClientTasks }>> {
  const userId = await requireUserId();
  const { data, error } = await getSupabase()
    .from('meetings')
    .select('id, options')
    .eq('source', 'bot')
    .eq('status', 'completed')
    .not('options->clientTasks', 'is', null)
    .eq('user_id', userId)
    .is('deleted_at', null)
    .limit(50);
  if (error) {
    console.error('[meetingsRepo] listBotClientTasks:', error.message);
    return [];
  }
  return (await openMeetingRows(data ?? [], userId))
    .map((row: any) => ({ meetingId: row.id as string, tasks: parseOptions(row.options).clientTasks }))
    .filter((row): row is { meetingId: string; tasks: BotClientTasks } => Boolean(row.tasks));
}

/**
 * Transkriptteki bir konuşmacı etiketini ("Konuşmacı 1") bir kişiye bağlar;
 * `personId` null ise eşlemeyi kaldırır. Kişi toplantıya katılımcı olarak da
 * eklenir — profilinde bu toplantı ve alıntıları görünsün.
 */
export async function setSpeakerPerson(
  meetingId: string,
  speakerLabel: string,
  personId: string | null
): Promise<void> {
  const label = speakerLabel.trim();
  if (!label) throw new Error(t('Geçersiz konuşmacı.', 'Invalid speaker.'));

  // Eşleme konuşmacı adlarını taşıyor: 3. seviyede şifreli (bağlam: toplantı).
  const userId = await requireUserId();
  const [current, sealer] = await Promise.all([
    getSupabase().from('meetings').select('id, options').eq('id', meetingId).single(),
    contentSealer(userId, meetingId),
  ]);
  if (current.error || !current.data) {
    throw new Error(t('Toplantı okunamadı.', 'Could not read the meeting.'));
  }
  const opened = parseOptions((await openMeetingRow(current.data, userId)).options);
  const map = { ...(opened.speakerMap ?? {}) };
  if (personId) map[label] = personId;
  else delete map[label];
  const speakerMap = Object.keys(map).length > 0 ? ((await sealer.json('meetings.speaker_map', map)) as Record<string, string>) : undefined;
  await patchOptions(meetingId, (options) => {
    const next: MeetingOptions = { ...options, speakerMap };
    // Daha düşük seviyede yazıldıysa işaret de iner (dönüştürme işi yeniden ele alır).
    if (next.enc && next.enc > sealer.level) next.enc = sealer.enabled ? (sealer.level as 1 | 2 | 3) : undefined;
    return next;
  });

  if (personId) {
    const names = await getPeopleNames([personId]);
    const name = names.get(personId);
    if (!name) throw new Error(t('Kişi bulunamadı.', 'Person not found.'));
    await resolveParticipants(meetingId, [{ name, personId }], 'merge');
  }
}
